import React, { useState, useCallback, useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../styles/EditArticlePage.css";
import { useTranslation } from "react-i18next";
import DemoAIAnalysisPanel from "../components/DemoAIAnalysisPanel.js";
import InlineMarkupEditor from "../components/InlineMarkupEditor.js";

const DemoEditArticlePage = () => {
  const { t } = useTranslation();
  const [title, setTitle] = useState("Demo Article - Test the AI Writing Assistant");
  const [content, setContent] = useState("<p>Start writing your article here to test the AI analysis features...</p>");
  const [category, setCategory] = useState("Tech");
  const [language, setLanguage] = useState("English");
  const [articleImage, setArticleImage] = useState("");
  const [inlineErrors, setInlineErrors] = useState([]);
  const [showMarkups, setShowMarkups] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleContentChange = useCallback((htmlValue, plainText) => {
    setContent(htmlValue);
  }, []);

  const handleInlineErrorsUpdate = useCallback((errors) => {
    setInlineErrors(errors || []);
    if (errors && errors.length > 0) {
      setShowMarkups(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Demo mode - just show success message without saving
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: "1" }, { header: "2" }, { font: [] }],
        [{ size: [] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"]
      ]
    }
  }), []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setArticleImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="article-edit-container">
      <div className="demo-banner">
        <h2>🚀 Demo Mode - Article Writing with AI Assistant</h2>
        <p>Test the AI-powered writing assistant without login. Your content won't be saved.</p>
      </div>
      
      {success && <p className="success-message">Demo article processed! (Not saved in demo mode)</p>}
      
      <form onSubmit={handleSubmit} className="article-edit-form">
        <div>
          <label htmlFor="title">Article Title</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>
        
        <div>
          <div className="content-header">
            <label htmlFor="content">Article Content</label>
            {inlineErrors.length > 0 && (
              <div className="markup-controls">
                <label className="markup-toggle">
                  <input
                    type="checkbox"
                    checked={showMarkups}
                    onChange={(e) => setShowMarkups(e.target.checked)}
                  />
                  Show inline markups ({inlineErrors.length})
                </label>
              </div>
            )}
          </div>
          <div className="editor-container">
            <InlineMarkupEditor
              content={content}
              onChange={handleContentChange}
              inlineErrors={inlineErrors}
              showMarkups={showMarkups}
              modules={quillModules}
              placeholder="Start writing your article to test the AI analysis..."
            />
          </div>
        </div>

        <div>
          <label htmlFor="category">Category</label>
          <select id="category" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="Tech">Technology</option>
            <option value="Sports">Sports</option>
            <option value="TDAH">ADHD</option>
            <option value="Education">Education</option>
            <option value="Health">Health</option>
            <option value="Mental Health">Mental Health</option>
          </select>
        </div>

        <div>
          <label htmlFor="language">Language</label>
          <select id="language" value={language} onChange={(e) => setLanguage(e.target.value)}>
            <option value="English">English</option>
            <option value="Portuguese">Portuguese</option>
            <option value="Spanish">Spanish</option>
          </select>
        </div>

        <div>
          <label htmlFor="imageUpload">Article Image (Optional)</label>
          <input type="file" id="imageUpload" accept="image/*" onChange={handleImageUpload} />
        </div>

        <button type="submit" className="submit-button">
          Test Article Processing (Demo)
        </button>
      </form>

      {/* AI Analysis Panel - Always show when there's content */}
      {content && content.trim().length > 0 && (
        <DemoAIAnalysisPanel 
          content={content}
          onAnalysisComplete={(analysis) => {
            console.log('Demo analysis completed:', analysis);
          }}
          onInlineErrorsUpdate={handleInlineErrorsUpdate}
        />
      )}
    </div>
  );
};

export default DemoEditArticlePage;
